/**
 * Xossa formulalarini hisoblash. Har funksiya SI qaytaradi.
 * Oraliqdan chiqish xato emas — ogohlantirish (extrapolyatsiya).
 */
import { warn, type WarningSink } from '../types';
import type {
  AntoineProp,
  PolyProp,
  ShomateProp,
  SutherlandProp,
  TempRange,
  VogelProp,
} from './types';

export const MMHG_TO_PA = 133.322368;
export const R_GAS = 8.314462618; // J/(mol·K)

export function checkRange(
  range: TempRange,
  T: number,
  ctx: { substance: string; property: string },
  warnings?: WarningSink,
): void {
  if (T < range[0] || T > range[1]) {
    warn(warnings, 'property.outOfRange', 'info', {
      substance: ctx.substance,
      property: ctx.property,
      T: Math.round(T),
      Tmin: range[0],
      Tmax: range[1],
    });
  }
}

/** Polinom: Σ c_i x^i, x = T[K] yoki t[°C] */
export function evalPoly(p: PolyProp, T: number): number {
  const x = p.variable === 'C' ? T - 273.15 : T;
  let y = 0;
  let xn = 1;
  for (const c of p.coeffs) {
    y += c * xn;
    xn *= x;
  }
  return y;
}

/** Shomate: J/(mol·K) → J/(kg·K) */
export function evalShomate(p: ShomateProp, T: number, molarMass: number): number {
  const t = T / 1000;
  const cpMolar = p.A + p.B * t + p.C * t * t + p.D * t * t * t + p.E / (t * t);
  return cpMolar / molarMass;
}

/** Antoine → Pa */
export function evalAntoine(p: AntoineProp, T: number): number {
  const tC = T - 273.15;
  const log10P = p.A - p.B / (p.C + tC);
  return Math.pow(10, log10P) * MMHG_TO_PA;
}

/** Antoine teskari: P[Pa] → T[K] (analitik) */
export function invertAntoine(p: AntoineProp, P: number): number {
  const log10P = Math.log10(P / MMHG_TO_PA);
  const tC = p.B / (p.A - log10P) - p.C;
  return tC + 273.15;
}

/** Vogel/Andrade → Pa·s */
export function evalVogel(p: VogelProp, T: number): number {
  return p.A * Math.exp(p.B / (T - p.C));
}

/** Sutherland → Pa·s */
export function evalSutherland(p: SutherlandProp, T: number): number {
  return p.mu0 * ((p.T0 + p.S) / (T + p.S)) * Math.pow(T / p.T0, 1.5);
}

/**
 * ∫_{a}^{b} g(T) dT — kompozit 3-nuqtali Gauss-Legendre.
 * 5-darajali polinom uchun aniq; Shomate E/t² hadi uchun ~1e-9 nisbiy.
 */
export function integrate1D(g: (T: number) => number, a: number, b: number, segments = 8): number {
  if (a === b) return 0;
  const nodes = [-Math.sqrt(3 / 5), 0, Math.sqrt(3 / 5)];
  const weights = [5 / 9, 8 / 9, 5 / 9];
  const h = (b - a) / segments;
  let sum = 0;
  for (let s = 0; s < segments; s++) {
    const lo = a + s * h;
    const mid = lo + 0.5 * h;
    for (let k = 0; k < 3; k++) {
      sum += (weights[k] as number) * g(mid + 0.5 * h * (nodes[k] as number));
    }
  }
  return sum * 0.5 * h;
}
